const FIELD_LABELS = {
  name: 'Nombre',
  secondName: 'Segundo nombre',
  lastName: 'Apellido',
  maternalLastName: 'Apellido materno',
  document: 'Documento',
  documentTypeId: 'Tipo de documento',
  email: 'Email',
  phone: 'Teléfono',
  cellPhone: 'Celular',
  birthDate: 'Fecha de nacimiento',
  birthPlace: 'Lugar de nacimiento',
  sexId: 'Sexo',
  enabled: 'Estado',
  username: 'Usuario',
  password: 'Contraseña',
  roleIds: 'Roles',
  profiles: 'Perfiles',
  recordType: 'Tipo de marca',
  amount: 'Monto',
  salary: 'Salario',
  employeeTypeId: 'Tipo de empleado',
  position: 'Cargo',
  department: 'Departamento',
  description: 'Descripción',
  title: 'Título',
  notes: 'Notas',
  street: 'Calle',
  number: 'Número',
  apartment: 'Apartamento',
  city: 'Ciudad',
  locality: 'Localidad',
  country: 'País',
  emergencyContact: 'Contacto de emergencia',
  emergencyContactPhone: 'Tel. emergencia',
  medicalInsurance: 'Mutualista',
  observations: 'Observaciones',
  school: 'Colegio',
  orientation: 'Orientación',
  employeeTitle: 'Cargo',
  hireDate: 'Fecha de ingreso',
};

const AUDIT_IGNORE_LEAF_KEYS = new Set([
  'id', 'personId', 'userId', 'addressId', 'studentId', 'employeeId',
  'createdDate', 'updatedDate', 'createdBy', 'updatedBy', 'form_69A', 'photo',
  'user', 'person', 'employee', 'student', 'address', 'sex', 'documentType',
]);

function shouldIgnoreKey(key) {
  const parts = key.split('.');
  return parts.some((p) => AUDIT_IGNORE_LEAF_KEYS.has(p));
}

function normalizeForCompare(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'boolean') return value ? '1' : '0';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    const t = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10);
    return t;
  }
  return String(value);
}

const HIDDEN_VALUE_PATTERNS = [/^data:image/i, /^\[omitido\]$/i, /^\[redacted\]$/i, /^\[contenido omitido\]$/i];

export function parseAuditJson(value) {
  if (value == null) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

function humanizeKey(key) {
  const leaf = key.includes('.') ? key.split('.').pop() : key;
  if (FIELD_LABELS[leaf]) return FIELD_LABELS[leaf];
  return leaf
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

export function formatAuditValue(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    if (HIDDEN_VALUE_PATTERNS.some((p) => p.test(value))) return '—';
    if (value.startsWith('data:image') || value.length > 500) return '[omitido]';
    if (value.length > 80) return `${value.slice(0, 80)}…`;
    return value;
  }
  if (Array.isArray(value)) {
    if (!value.length) return '—';
    return value.map((v) => formatAuditValue(v)).join(', ');
  }
  return String(value);
}

function flattenValues(obj) {
  if (!obj || typeof obj !== 'object') return {};
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key.startsWith('_') || shouldIgnoreKey(key)) continue;
    if (key.includes('.')) {
      result[key] = value;
      continue;
    }
    if (value != null && typeof value === 'object' && !Array.isArray(value)) {
      for (const [nestedKey, nestedValue] of Object.entries(flattenValues(value))) {
        result[`${key}.${nestedKey}`] = nestedValue;
      }
    } else if (value !== undefined) {
      result[key] = value;
    }
  }
  return result;
}

export function isLegacyAuditLog(log) {
  const action = log?.action;
  const oldValues = parseAuditJson(log?.oldValues ?? log?.old_values);
  const newValues = parseAuditJson(log?.newValues ?? log?.new_values);
  return action === 'UPDATE' && !oldValues && !!newValues;
}

export function getAuditDiffs(log) {
  const oldValues = parseAuditJson(log?.oldValues ?? log?.old_values);
  const newValues = parseAuditJson(log?.newValues ?? log?.new_values);

  if (isLegacyAuditLog(log)) {
    return [];
  }

  const oldFlat = flattenValues(oldValues);
  const newFlat = flattenValues(newValues);
  const keys = [...new Set([...Object.keys(oldFlat), ...Object.keys(newFlat)])];

  const diffs = keys
    .map((key) => {
      if (shouldIgnoreKey(key)) return null;
      const before = formatAuditValue(oldFlat[key]);
      const after = formatAuditValue(newFlat[key]);
      if (normalizeForCompare(oldFlat[key]) === normalizeForCompare(newFlat[key])) return null;
      if (before === after) return null;
      return { key, label: humanizeKey(key), before, after };
    })
    .filter(Boolean);

  if (diffs.length) return diffs;

  if (log?.action === 'CREATE' && Object.keys(newFlat).length) {
    return Object.keys(newFlat)
      .filter((k) => !shouldIgnoreKey(k))
      .slice(0, 5)
      .map((key) => ({
        key,
        label: humanizeKey(key),
        before: null,
        after: formatAuditValue(newFlat[key]),
      }));
  }

  return [];
}

export function buildChangePreview(log) {
  if (isLegacyAuditLog(log)) {
    return 'Registro anterior (sin comparación)';
  }
  const diffs = getAuditDiffs(log);
  if (!diffs.length) return '—';
  const first = diffs[0];
  if (first.before && first.before !== '—') {
    return `${first.label}: ${first.before} → ${first.after}`;
  }
  const extra = diffs.length > 1 ? ` (+${diffs.length - 1})` : '';
  return `${first.label}: ${first.after}${extra}`;
}
